import { Container, Badge, Button, Row, Col, Card, CardBody, CardTitle } from 'reactstrap';
import { useContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { confirmAlert } from 'react-confirm-alert';
import { Link } from 'react-router-dom';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import CommonHeader from '../../../components/Headers/CommonHeader';
import FranchiseeService from '../../../services/Franchisee';
import FranchiseeInterface, { validStatus, statusEnum } from '../../../interfaces/FranchiseeInterface';
import dateFormat from '../../../helpers/dateFormat';
import { FilterInterface, FilteredOptionsInterface } from '../../../entities/FilteredOptions';
import { PagedMetaInterface, createPagedMeta } from '../../../entities/PagedMeta';
import { OptionsPagedInterface, createOptionsPage } from '../../../entities/OptionsPaged';
import DataTable from '../../../components/Core/DataTable';
import isAdmin from '../../../helpers/isAdmin';

const FranchiseeList = () => {
  const { token, user } = useContext(AuthContext);
  const hasEditPermission = isAdmin(user);

  const columns = [
    {
      name: 'Name',
      route: '/franchisee',
      label: (row: any) => row.name,
      disabled: !hasEditPermission,
    },
    {
      name: 'Description',
      selector: (row: any) => row.obs,
      grow: 2,
    },
    {
      name: 'Status',
      selector: (row: any) => row.status,
      grow: 1,
      cell: (row: any) => (
        <Badge
          color=""
          className="badge-dot badge-lg"
          style={{ fontSize: '0.8125rem', verticalAlign: 'middle', lineHeight: 'normal' }}
        >
          <i
            className={`bg-${validStatus[row.status as statusEnum].color} mr-2`}
            style={{ verticalAlign: 'baseline' }}
          />
          {validStatus[row.status as statusEnum].value}
        </Badge>
      ),
    },
    {
      name: 'Created at',
      selector: (row: any) => row.createdAt,
      cell: (row: any) => dateFormat(row.createdAt),
      grow: 2,
    },
    {
      name: 'Updated at',
      selector: (row: any) => row.updatedAt,
      cell: (row: any) => dateFormat(row.updatedAt),
      grow: 2,
    },
    hasEditPermission && {
      name: 'Actions',
      cell: (row: any) => (
        <div className="d-flex justify-content-center">
          <Button
            outline
            color={row.integration && row.integration.type === 'trello' ? 'primary' : 'danger'}
            size="sm"
            style={{ padding: '0.1rem 0.4rem', border: 'none' }}
            title="Configure Support Integration"
            tag={Link}
            to={`/franchisee/${row.id}/integration`}
          >
            <i className="fas fa-headset" />
          </Button>
          <Button
            outline
            color="danger"
            size="sm"
            style={{ padding: '0.1rem 0.4rem', border: 'none' }}
            title="Delete"
            onClick={() => {
              deleteItemAlert(row.id);
            }}
          >
            <i className="far fa-trash-alt" />
          </Button>
        </div>
      ),
      width: '120px',
      center: true,
    },
  ];

  const [data, setData] = useState([] as any[]);
  const [loading, setLoading] = useState(false);
  const [pagedMeta, setPagedMeta] = useState(createPagedMeta());
  const [filteredOptions, setFilteredOptions] = useState({
    filters: [],
    options: createOptionsPage(pagedMeta),
  } as { filters: FilterInterface[]; options: OptionsPagedInterface });

  useEffect(() => {
    loadList(filteredOptions);
  }, [filteredOptions]);

  const loadList = async (filteredOptions: FilteredOptionsInterface) => {
    try {
      setLoading(true);
      const result = await FranchiseeService(token || '').list(filteredOptions);
      const data: FranchiseeInterface[] = result.data;
      setData(data);
      const meta: PagedMetaInterface = result.meta;
      if (meta) {
        setPagedMeta(meta);
      }
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Error fetching list');
      return toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (text: string) => {
    const filterFirstName = {
      field: 'name',
      operation: 'ilike',
      value: text || '',
    };
    const newFilteredOptions = {
      filters: [filterFirstName],
      options: filteredOptions.options,
    };
    newFilteredOptions.options.page = 1;
    setFilteredOptions(newFilteredOptions);
  };

  const deleteItemAlert = async (id: string) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <Container fluid>
            <Card>
              <CardBody>
                <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
                  Delete
                </CardTitle>
                <Row className="mt-3">
                  <Col md="12 text-center">
                    <span className="h2 font-weight-bold mb-0">{`Are you sure to delete item: ${id}?`}</span>
                  </Col>
                </Row>
                <Row className="mt-5">
                  <Col md="6 text-center">
                    <Button
                      color="primary"
                      href="#"
                      onClick={() => {
                        deleteItem(id);
                        onClose();
                      }}
                      size="lg"
                      style={{ minWidth: '100px' }}
                    >
                      Yes
                    </Button>
                  </Col>
                  <Col md="6 text-center">
                    <Button color="danger" href="#" onClick={onClose} size="lg" style={{ minWidth: '100px' }}>
                      No
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Container>
        );
      },
    });
  };

  const deleteItem = async (id: string) => {
    try {
      setLoading(true);
      await FranchiseeService(token || '').delete(id);
      toast.success(`Deleted successfully!`);
      loadList(filteredOptions);
    } catch (e: any) {
      const message = String(e?.response?.data?.message || `Error deleting item: ${id}`);
      return toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePerRowsChange = async (perPage: number) => {
    const newOptions = {
      ...pagedMeta,
      perPage,
    };
    const newFilteredOptions = {
      filters: filteredOptions.filters,
      options: createOptionsPage(newOptions),
    };
    setFilteredOptions(newFilteredOptions);
  };

  const handlePageChange = async (page: number) => {
    const newOptions = {
      ...pagedMeta,
      currentPage: page,
    };
    const newFilteredOptions = {
      filters: filteredOptions.filters,
      options: createOptionsPage(newOptions),
    };
    setFilteredOptions(newFilteredOptions);
  };

  return (
    <>
      <CommonHeader />
      <Container className="mt--8" fluid>
        <DataTable
          title="Franchisee List"
          columns={columns}
          data={data}
          totalRows={pagedMeta.total}
          loading={loading}
          button={hasEditPermission}
          buttonUrl="/franchisee/new"
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          search={true}
          onSearch={handleSearch}
        />
      </Container>
    </>
  );
};

export default FranchiseeList;
